import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import {
  calculateLienAvoidance,
  downloadLienAvoidancePdf,
  formatLienCurrency,
  prepareLienAvoidancePdfs,
} from "./lienAvoidanceGenerator";
import { filingDebtors as getFilingDebtors } from "../utils/debtors";

const API_URL = process.env.REACT_APP_API_URL;

const lower = (value) => String(value || "").trim().toLowerCase();
const amount = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, number) : 0;
};

function debtorName(debtor) {
  return [
    debtor?.first_name,
    debtor?.second_name || debtor?.middle_name,
    debtor?.last_name,
    debtor?.suffix,
  ].filter(Boolean).join(" ");
}

function attorneyName(attorney) {
  return [attorney?.first_name, attorney?.second_name, attorney?.last_name]
    .filter(Boolean)
    .join(" ");
}

function isJudgmentLienCreditor(creditor) {
  if (lower(creditor?.debt_type) !== "secured") return false;
  const accountType = lower(creditor?.account_type).replace(/[_-]+/g, " ");
  return accountType === "judgment lien" || lower(creditor?.nature_of_lien) === "judgment";
}

function isRealPropertyLink(link) {
  return lower(link?.asset_type).replace(/[^a-z]/g, "") === "realproperty";
}

function propertyDescription(property) {
  if (!property) return "";
  const cityLine = [
    property.city ? `${property.city},` : "",
    property.state,
    property.zip_code,
  ].filter(Boolean).join(" ");
  return [property.street1, property.street2, cityLine].filter(Boolean).join(", ");
}

function propertyLabel(property) {
  const description = propertyDescription(property);
  return description || `Real property ${property?.id || ""}`;
}

function creditorAddress(creditor) {
  const cityLine = [
    creditor?.city ? `${creditor.city},` : "",
    creditor?.state,
    creditor?.zip_code,
  ].filter(Boolean).join(" ");
  return [creditor?.street1, creditor?.street2, cityLine]
    .filter(Boolean)
    .join(", ") || creditor?.full_address || "No mailing address entered";
}

function natureOfLien(creditor) {
  const accountType = String(creditor?.account_type || "").trim();
  if (accountType && lower(accountType) !== "secured") return accountType;
  const nature = lower(creditor?.nature_of_lien);
  if (nature === "agreement") return "Mortgage or consensual lien";
  if (nature === "judgment") return "Judgment lien";
  if (nature === "statutory") return "Statutory lien";
  return creditor?.lien_description || "Secured lien";
}

function extractBookAndPage(creditor) {
  const description = String(creditor?.lien_description || "");
  const match = description.match(/book\s*[:#]?\s*([\w-]+)\s*[,/]?\s*page\s*[:#]?\s*([\w-]+)/i);
  return match ? { book: match[1], page: match[2] } : { book: "", page: "" };
}

function datePriority(otherLienDate, judgmentLienDate) {
  if (!otherLienDate || !judgmentLienDate) return "Date priority needs confirmation";
  if (otherLienDate < judgmentLienDate) return "Earlier lien";
  if (otherLienDate > judgmentLienDate) return "Later lien";
  return "Same date - confirm priority";
}

function addTennesseeCodePrefix(value) {
  const code = String(value || "")
    .trim()
    .replace(/^(?:tenn(?:essee)?|tn)\.?\s+code\s+ann\.?\s*§?\s*/i, "")
    .trim();
  return `Tenn. Code Ann. §${code ? ` ${code}` : " "}`;
}

function InputField({ label, value, onChange, required = false, type = "text", help = "", min, step }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}{required ? " *" : ""}
      </span>
      <input
        type={type}
        min={min}
        step={step}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
      />
      {help && <span className="mt-1 block text-xs text-gray-500">{help}</span>}
    </label>
  );
}

function CurrencyInput({ label, value, onChange, required = false, help = "" }) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm font-medium text-gray-700">
        {label}{required ? " *" : ""}
      </span>
      <div className="relative">
        <span className="absolute left-3 top-2 text-gray-500">$</span>
        <input
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full rounded-md border border-gray-300 bg-white p-2 pl-7 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
        />
      </div>
      {help && <span className="mt-1 block text-xs text-gray-500">{help}</span>}
    </label>
  );
}

export default function LienAvoidance() {
  const { caseId } = useParams();
  const [caseData, setCaseData] = useState(null);
  const [creditors, setCreditors] = useState([]);
  const [lawsuits, setLawsuits] = useState([]);
  const [attorneys, setAttorneys] = useState([]);
  const [selectedCreditorId, setSelectedCreditorId] = useState("");
  const [selectedPropertyId, setSelectedPropertyId] = useState("");
  const [selectedAttorneyId, setSelectedAttorneyId] = useState("");
  const [selectedLawsuitId, setSelectedLawsuitId] = useState("");
  const [details, setDetails] = useState({
    lienAmount: "",
    lienDate: "",
    propertyDescription: "",
    propertyValue: "",
    exemptionAmount: "",
    exemptionCode: "",
    civilCaseNumber: "",
    book: "",
    page: "",
    county: "",
  });
  const [otherLiens, setOtherLiens] = useState([]);
  const [serviceMethods, setServiceMethods] = useState({});
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [preparedPdfs, setPreparedPdfs] = useState(null);
  const [savedPackets, setSavedPackets] = useState([]);
  const [downloadingPacketId, setDownloadingPacketId] = useState("");
  const [deletingPacketId, setDeletingPacketId] = useState("");

  useEffect(() => {
    let cancelled = false;

    async function loadPage() {
      setLoading(true);
      setError("");
      try {
        const token = localStorage.getItem("accessToken");
        const headers = { Authorization: `Bearer ${token}` };
        const [
          overviewResponse,
          creditorsResponse,
          accountResponse,
          lawsuitsResponse,
          packetsResponse,
        ] = await Promise.all([
          fetch(`${API_URL}/case/${caseId}/overview/`, { headers }),
          fetch(`${API_URL}/case/${caseId}/creditors/`, { headers }),
          fetch(`${API_URL}/firm/account/`, { headers }),
          fetch(`${API_URL}/cases/${caseId}/sofa-lawsuits/`, { headers }),
          fetch(`${API_URL}/case/${caseId}/lien-avoidance-packets/`, { headers }),
        ]);

        if (!overviewResponse.ok) throw new Error("The case information could not be loaded.");
        if (!creditorsResponse.ok) throw new Error("The case creditors could not be loaded.");
        if (!accountResponse.ok) throw new Error("The firm attorney information could not be loaded.");
        if (!lawsuitsResponse.ok) throw new Error("The case lawsuit records could not be loaded.");
        if (!packetsResponse.ok) throw new Error("The saved lien-avoidance packets could not be loaded.");

        const [overview, caseCreditors, account, caseLawsuits, packetHistory] = await Promise.all([
          overviewResponse.json(),
          creditorsResponse.json(),
          accountResponse.json(),
          lawsuitsResponse.json(),
          packetsResponse.json(),
        ]);
        if (cancelled) return;

        const attorneyList = Array.isArray(account?.attorneys) ? account.attorneys : [];
        setCaseData(overview);
        setCreditors(Array.isArray(caseCreditors) ? caseCreditors : []);
        setLawsuits(
          Array.isArray(caseLawsuits)
            ? caseLawsuits
            : (Array.isArray(caseLawsuits?.results) ? caseLawsuits.results : []),
        );
        setAttorneys(attorneyList);
        setSavedPackets(Array.isArray(packetHistory?.packets) ? packetHistory.packets : []);
        if (attorneyList.length === 1) setSelectedAttorneyId(String(attorneyList[0].id));
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || "The lien-avoidance information could not be loaded.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (caseId) loadPage();
    return () => { cancelled = true; };
  }, [caseId]);

  const eligibleCreditors = useMemo(
    () => creditors.filter(isJudgmentLienCreditor),
    [creditors],
  );
  const properties = useMemo(
    () => (Array.isArray(caseData?.real_properties) ? caseData.real_properties : []),
    [caseData],
  );
  const selectedCreditor = useMemo(
    () => eligibleCreditors.find((creditor) => String(creditor.id) === String(selectedCreditorId)) || null,
    [eligibleCreditors, selectedCreditorId],
  );
  const selectedProperty = useMemo(
    () => properties.find((property) => String(property.id) === String(selectedPropertyId)) || null,
    [properties, selectedPropertyId],
  );
  const selectedAttorney = useMemo(
    () => attorneys.find((attorney) => String(attorney.id) === String(selectedAttorneyId)) || null,
    [attorneys, selectedAttorneyId],
  );
  const selectedLawsuit = useMemo(
    () => lawsuits.find((lawsuit) => String(lawsuit.id) === String(selectedLawsuitId)) || null,
    [lawsuits, selectedLawsuitId],
  );
  const linkedPropertyIds = useMemo(() => new Set(
    (selectedCreditor?.asset_links || [])
      .filter(isRealPropertyLink)
      .map((link) => String(link.object_id)),
  ), [selectedCreditor]);

  useEffect(() => {
    if (eligibleCreditors.length === 1 && !selectedCreditorId) {
      setSelectedCreditorId(String(eligibleCreditors[0].id));
    }
  }, [eligibleCreditors, selectedCreditorId]);

  useEffect(() => {
    if (!selectedCreditor) {
      setSelectedPropertyId("");
      setServiceMethods({});
      return;
    }

    const linked = properties.filter((property) => linkedPropertyIds.has(String(property.id)));
    const recording = extractBookAndPage(selectedCreditor);
    setSelectedPropertyId(linked.length === 1 ? String(linked[0].id) : "");
    setSelectedLawsuitId("");
    setServiceMethods({ [String(selectedCreditor.id)]: "USM" });
    setDetails((current) => ({
      ...current,
      lienAmount: selectedCreditor.balance ?? "",
      lienDate: selectedCreditor.date_opened || "",
      civilCaseNumber: selectedCreditor.lien_case_number || selectedCreditor.account_number || "",
      book: selectedCreditor.lien_book || recording.book,
      page: selectedCreditor.lien_page || recording.page,
      county: selectedCreditor.lien_county || "",
    }));
  }, [selectedCreditor, properties, linkedPropertyIds]);

  useEffect(() => {
    if (!selectedLawsuit) return;
    setDetails((current) => ({
      ...current,
      civilCaseNumber: selectedLawsuit.case_number || "",
    }));
  }, [selectedLawsuit]);

  useEffect(() => {
    if (!selectedProperty || !selectedCreditor) {
      setOtherLiens([]);
      return;
    }

    const propertyId = String(selectedProperty.id);
    const judgmentDate = selectedCreditor.date_opened || "";
    const detectedLiens = creditors
      .filter((creditor) => (
        String(creditor.id) !== String(selectedCreditor.id)
        && lower(creditor.debt_type) === "secured"
        && (creditor.asset_links || []).some((link) => (
          isRealPropertyLink(link) && String(link.object_id) === propertyId
        ))
      ))
      .map((creditor) => ({
        id: String(creditor.id),
        name: creditor.account_name || "Other lienholder",
        nature: natureOfLien(creditor),
        date: creditor.date_opened || "",
        amount: creditor.balance ?? "",
        included: !(judgmentDate && creditor.date_opened) || creditor.date_opened < judgmentDate,
        automatic: true,
      }))
      .sort((left, right) => (
        (left.date || "9999-12-31").localeCompare(right.date || "9999-12-31")
        || left.name.localeCompare(right.name)
      ));

    setDetails((current) => ({
      ...current,
      propertyDescription: propertyDescription(selectedProperty),
      propertyValue: selectedProperty.value_ownership_portion ?? selectedProperty.value_total ?? "",
      exemptionAmount: selectedProperty.exemption_amount ?? "",
      exemptionCode: selectedProperty.exemption_code || "",
      county: selectedCreditor.lien_county || selectedProperty.county || "",
    }));
    setOtherLiens(detectedLiens);
  }, [selectedProperty, selectedCreditor, creditors]);

  useEffect(() => {
    setPreparedPdfs(null);
    setSuccess("");
  }, [selectedCreditorId, selectedPropertyId, selectedAttorneyId, details, otherLiens, serviceMethods]);

  const includedOtherLiens = useMemo(
    () => otherLiens.filter((lien) => lien.included !== false),
    [otherLiens],
  );
  const otherLiensTotal = useMemo(
    () => includedOtherLiens.reduce((total, lien) => total + amount(lien.amount), 0),
    [includedOtherLiens],
  );
  const serviceRecipients = useMemo(() => creditors
    .filter((creditor) => serviceMethods[String(creditor.id)])
    .map((creditor) => ({
      creditorId: String(creditor.id),
      name: creditor.account_name || "Creditor",
      street1: creditor.street1 || "",
      street2: creditor.street2 || "",
      city: creditor.city || "",
      state: creditor.state || "",
      zipCode: creditor.zip_code || "",
      addressLine: creditorAddress(creditor),
      method: serviceMethods[String(creditor.id)],
    })), [creditors, serviceMethods]);
  const calculation = useMemo(() => calculateLienAvoidance({
    lienAmount: details.lienAmount,
    otherLiensTotal,
    exemptionAmount: details.exemptionAmount,
    propertyValue: details.propertyValue,
  }), [details.lienAmount, details.exemptionAmount, details.propertyValue, otherLiensTotal]);

  const validationErrors = useMemo(() => {
    const errors = [];
    if (!selectedCreditor) errors.push("Select a secured judgment lien creditor.");
    if (!selectedProperty) errors.push("Select the real property encumbered by the lien.");
    if (!selectedAttorney) errors.push("Select the attorney who will sign the documents.");
    if (selectedCreditor && !serviceMethods[String(selectedCreditor.id)]) {
      errors.push("Choose USM or Certified Mail for the judgment lien creditor.");
    }
    if (!details.propertyDescription.trim()) errors.push("Enter a property description.");
    if (!details.exemptionCode.trim()) errors.push("Enter the statutory exemption used for the property.");
    if (!details.civilCaseNumber.trim()) errors.push("Enter the civil case number.");
    if (!details.lienDate) errors.push("Enter the judgment lien date for the priority comparison.");
    if (!details.book.trim()) errors.push("Enter the judgment book number.");
    if (!details.page.trim()) errors.push("Enter the judgment page number.");
    if (!details.county.trim()) errors.push("Enter the recording county.");
    if (amount(details.lienAmount) <= 0) errors.push("Enter a judgment lien amount greater than zero.");
    if (amount(details.exemptionAmount) <= 0) errors.push("Enter the claimed exemption amount.");
    if (calculation.avoidableAmount <= 0 && selectedCreditor && selectedProperty) {
      errors.push("The current figures do not produce an avoidable lien amount.");
    }
    includedOtherLiens.forEach((lien, index) => {
      if (!String(lien.name || "").trim()) errors.push(`Enter a name for other lien ${index + 1}.`);
      if (!lien.date) errors.push(`Enter the lien date for other lien ${index + 1}.`);
    });
    serviceRecipients.forEach((recipient) => {
      if (!recipient.street1 || !recipient.city || !recipient.state || !recipient.zipCode) {
        errors.push(`Complete the mailing address for ${recipient.name} before including that creditor in service.`);
      }
    });
    return errors;
  }, [
    selectedCreditor,
    selectedProperty,
    selectedAttorney,
    details,
    calculation.avoidableAmount,
    includedOtherLiens,
    serviceMethods,
    serviceRecipients,
  ]);

  const selectedCreditorLinkedToProperty = Boolean(
    selectedProperty && linkedPropertyIds.has(String(selectedProperty.id)),
  );
  const filingDebtors = useMemo(() => (
    getFilingDebtors(caseData?.debtors)
  ), [caseData]);

  const updateDetail = (field, value) => {
    setDetails((current) => ({ ...current, [field]: value }));
  };

  const updateJudgmentLienDate = (value) => {
    updateDetail("lienDate", value);
    setOtherLiens((current) => current.map((lien) => (
      lien.automatic
        ? { ...lien, included: !value || !lien.date || lien.date < value }
        : lien
    )));
  };

  const updateOtherLien = (index, field, value) => {
    setOtherLiens((current) => current.map((lien, lienIndex) => (
      lienIndex === index
        ? {
          ...lien,
          [field]: value,
          ...(field === "date" && lien.automatic
            ? { included: !details.lienDate || !value || value < details.lienDate }
            : {}),
        }
        : lien
    )));
  };

  const addOtherLien = () => {
    setOtherLiens((current) => [
      ...current,
      {
        id: `manual-${Date.now()}-${current.length}`,
        name: "",
        nature: "",
        date: "",
        amount: "",
        included: true,
        automatic: false,
      },
    ]);
  };

  async function handleGenerate() {
    if (validationErrors.length) return;
    setGenerating(true);
    setError("");
    setSuccess("");
    try {
      const pdfs = await prepareLienAvoidancePdfs({
        caseData,
        creditor: selectedCreditor,
        property: selectedProperty,
        attorney: selectedAttorney,
        details: {
          ...details,
          otherLiens: includedOtherLiens,
          serviceRecipients,
        },
      });
      const token = localStorage.getItem("accessToken");
      const creditorResponse = await fetch(
        `${API_URL}/case/${caseId}/creditors/${selectedCreditor.id}/`,
        {
          method: "PATCH",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            nature_of_lien: "judgment",
            lien_case_number: details.civilCaseNumber,
            lien_book: details.book,
            lien_page: details.page,
            lien_county: details.county,
          }),
        },
      );
      if (!creditorResponse.ok) {
        throw new Error("The judgment recording details could not be saved to the creditor.");
      }
      const formData = new FormData();
      formData.append("creditor_id", String(selectedCreditor.id));
      formData.append("property_id", String(selectedProperty.id));
      formData.append("service_recipients", JSON.stringify(serviceRecipients.map((recipient) => ({
        creditor_id: recipient.creditorId,
        method: recipient.method,
      }))));
      formData.append("details", JSON.stringify({
        ...details,
        otherLiens: includedOtherLiens,
        calculation,
      }));
      formData.append(
        "motion",
        new Blob([pdfs.motion.bytes], { type: "application/pdf" }),
        pdfs.motion.filename,
      );
      formData.append(
        "order",
        new Blob([pdfs.order.bytes], { type: "application/pdf" }),
        pdfs.order.filename,
      );

      const response = await fetch(`${API_URL}/case/${caseId}/lien-avoidance-packets/`, {
        method: "POST",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: formData,
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "The lien-avoidance service packet could not be saved.");
      }

      const savedPacket = payload.packet;
      setPreparedPdfs({ ...pdfs, packet: savedPacket });
      setSavedPackets((current) => [
        savedPacket,
        ...current.filter((packet) => packet.id !== savedPacket.id),
      ]);
      setSuccess("The packet was saved with its envelopes and added to Today's ToDos for service.");
    } catch (generationError) {
      console.error(generationError);
      setError(generationError.message || "The lien-avoidance PDFs could not be generated.");
    } finally {
      setGenerating(false);
    }
  }

  async function downloadStoredPacket(packet) {
    setDownloadingPacketId(packet.id);
    setError("");
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`${API_URL}${packet.download_url}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "The saved packet could not be downloaded.");
      }
      const bytes = new Uint8Array(await response.arrayBuffer());
      downloadLienAvoidancePdf({
        bytes,
        filename: packet.filename || "lien-avoidance-service-packet.zip",
        contentType: "application/zip",
      });
    } catch (downloadError) {
      setError(downloadError.message || "The saved packet could not be downloaded.");
    } finally {
      setDownloadingPacketId("");
    }
  }

  async function deleteStoredPacket(packet) {
    const confirmed = window.confirm(
      "Delete this saved lien-avoidance packet and its service todo? This cannot be undone.",
    );
    if (!confirmed) return;

    setDeletingPacketId(packet.id);
    setError("");
    setSuccess("");
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${API_URL}/case/${caseId}/lien-avoidance-packets/${packet.id}/`,
        {
          method: "DELETE",
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "The saved packet could not be deleted.");
      }

      setSavedPackets((current) => current.filter((item) => item.id !== packet.id));
      setPreparedPdfs((current) => (
        current?.packet?.id === packet.id ? null : current
      ));
      setSuccess("The saved packet and its service todo were deleted.");
    } catch (deleteError) {
      setError(deleteError.message || "The saved packet could not be deleted.");
    } finally {
      setDeletingPacketId("");
    }
  }

  return (
    <section className="mx-auto mt-12 max-w-5xl space-y-8 pb-16">
      <header className="rounded-md bg-gray-100 p-4">
        <h2 className="text-2xl font-semibold">Motion to Avoid Judicial Lien</h2>
      </header>

      <p className="text-gray-600">
        Select the secured judgment lien creditor and the affected real property. The property
        value, exemption, and other secured liens are filled from the case and remain editable
        before preparing the motion and proposed order.
      </p>

      {loading && <div className="rounded-md bg-blue-50 p-3 text-blue-800">Loading case information…</div>}
      {error && <div className="rounded-md bg-red-50 p-3 text-red-800">{error}</div>}
      {success && <div className="rounded-md bg-green-50 p-3 text-green-800">{success}</div>}

      {!loading && caseData && (
        <>
          <div className="rounded-lg border border-indigo-100 bg-white p-6 shadow-md">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Saved service packets</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Download a prior packet or delete one created by mistake. Deleting a packet also
                  removes its corresponding service todo.
                </p>
              </div>
              <span className="w-fit rounded-full bg-indigo-50 px-3 py-1 text-sm font-semibold text-indigo-700">
                {savedPackets.length} saved
              </span>
            </div>

            {savedPackets.length === 0 ? (
              <div className="mt-4 rounded-md border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">
                No lien-avoidance packets have been saved for this case yet.
              </div>
            ) : (
              <div className="mt-4 divide-y divide-gray-200 rounded-md border border-gray-200">
                {savedPackets.map((packet) => {
                  const isDeleting = deletingPacketId === packet.id;
                  const isDownloading = downloadingPacketId === packet.id;
                  return (
                    <div key={packet.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {packet.creditor_name || "Judgment lien creditor"}
                        </p>
                        {packet.property && (
                          <p className="mt-1 text-sm text-gray-600">{packet.property}</p>
                        )}
                        <p className="mt-1 text-sm text-gray-600">
                          {new Date(packet.created_at).toLocaleString()} · {packet.recipient_count} envelope{packet.recipient_count === 1 ? "" : "s"}
                        </p>
                        <p className={`mt-1 text-xs font-semibold ${packet.served_at ? "text-green-700" : "text-amber-700"}`}>
                          {packet.served_at ? "Service completed" : "Awaiting service"}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() => downloadStoredPacket(packet)}
                          disabled={isDownloading || isDeleting}
                          className="rounded-md border border-indigo-300 bg-white px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDownloading ? "Downloading…" : "Download packet"}
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteStoredPacket(packet)}
                          disabled={isDeleting || isDownloading}
                          className="rounded-md border border-red-300 bg-white px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          {isDeleting ? "Deleting…" : "Delete packet"}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="rounded-lg bg-white p-6 shadow-md">
            <h3 className="text-lg font-semibold text-gray-900">Case and selections</h3>
            <dl className="mt-4 grid grid-cols-1 gap-4 rounded-md bg-gray-50 p-4 text-sm sm:grid-cols-3">
              <div>
                <dt className="text-gray-500">Case</dt>
                <dd className="font-medium text-gray-900">{caseData.case_number || "Not assigned"}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Debtor(s)</dt>
                <dd className="font-medium text-gray-900">
                  {filingDebtors.map(debtorName).filter(Boolean).join(" & ") || "Not available"}
                </dd>
              </div>
              <div>
                <dt className="text-gray-500">Chapter</dt>
                <dd className="font-medium text-gray-900">{caseData.chapter || "Not available"}</dd>
              </div>
            </dl>

            <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Judgment lien creditor *</span>
                <select
                  value={selectedCreditorId}
                  onChange={(event) => setSelectedCreditorId(event.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900"
                >
                  <option value="">Select creditor…</option>
                  {eligibleCreditors.map((creditor) => (
                    <option key={creditor.id} value={creditor.id}>
                      {creditor.account_name} ({formatLienCurrency(creditor.balance)})
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Real property *</span>
                <select
                  value={selectedPropertyId}
                  onChange={(event) => setSelectedPropertyId(event.target.value)}
                  disabled={!selectedCreditor}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 disabled:bg-gray-100"
                >
                  <option value="">{selectedCreditor ? "Select property…" : "Choose creditor first"}</option>
                  {properties.map((property) => (
                    <option key={property.id} value={property.id}>
                      {propertyLabel(property)}{linkedPropertyIds.has(String(property.id)) ? " — linked" : ""}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700">Signing attorney *</span>
                <select
                  value={selectedAttorneyId}
                  onChange={(event) => setSelectedAttorneyId(event.target.value)}
                  className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900"
                >
                  <option value="">Select attorney…</option>
                  {attorneys.map((attorney) => (
                    <option key={attorney.id} value={attorney.id}>{attorneyName(attorney)}</option>
                  ))}
                </select>
              </label>
            </div>

            {!eligibleCreditors.length && (
              <div className="mt-5 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                No creditor is currently marked both secured and judgment lien. Update the creditor
                before preparing this motion.
              </div>
            )}
            {!properties.length && (
              <div className="mt-5 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                No real property is listed on this case.
              </div>
            )}
            {selectedProperty && !selectedCreditorLinkedToProperty && (
              <div className="mt-5 rounded-md bg-amber-50 p-3 text-sm text-amber-900">
                This property is not currently linked to the selected creditor. You can continue,
                but confirm that the judgment lien actually encumbers this property.
              </div>
            )}
          </div>

          {selectedCreditor && selectedProperty && (
            <>
              <div className="rounded-lg bg-white p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900">Judgment and property details</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Select a lawsuit record when one is available, then confirm the recording and priority details.
                </p>
                <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
                  <CurrencyInput
                    label="Amount of judgment lien"
                    value={details.lienAmount}
                    onChange={(value) => updateDetail("lienAmount", value)}
                    required
                  />
                  <InputField
                    label="Judgment lien date"
                    type="date"
                    value={details.lienDate}
                    onChange={updateJudgmentLienDate}
                    required
                    help="Filled from the creditor's Date Opened field when available. Confirm this is the lien or recording date."
                  />
                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-gray-700">
                      Civil case number from lawsuit record
                    </span>
                    <select
                      value={selectedLawsuitId}
                      onChange={(event) => {
                        const lawsuitId = event.target.value;
                        setSelectedLawsuitId(lawsuitId);
                        if (!lawsuitId) updateDetail("civilCaseNumber", selectedCreditor.account_number || "");
                      }}
                      className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    >
                      <option value="">Enter manually or use the creditor account number</option>
                      {lawsuits.map((lawsuit) => (
                        <option key={lawsuit.id} value={lawsuit.id}>
                          {lawsuit.case_number || "No case number"} — {lawsuit.case_title || "Untitled lawsuit"}
                        </option>
                      ))}
                    </select>
                    {!lawsuits.length && (
                      <span className="mt-1 block text-xs text-gray-500">
                        No lawsuit records are currently listed in the Statement of Financial Affairs.
                      </span>
                    )}
                  </label>
                  <InputField
                    label="Civil case number"
                    value={details.civilCaseNumber}
                    onChange={(value) => updateDetail("civilCaseNumber", value)}
                    required
                  />
                  <InputField
                    label="Recording county"
                    value={details.county}
                    onChange={(value) => updateDetail("county", value)}
                    required
                  />
                  <InputField
                    label="Judgment book"
                    value={details.book}
                    onChange={(value) => updateDetail("book", value)}
                    required
                    help="Enter only the book number."
                  />
                  <InputField
                    label="Judgment page"
                    value={details.page}
                    onChange={(value) => updateDetail("page", value)}
                    required
                    help="Enter only the page number."
                  />
                  <CurrencyInput
                    label="Value of debtor's interest in property"
                    value={details.propertyValue}
                    onChange={(value) => updateDetail("propertyValue", value)}
                    required
                    help="Uses ownership-portion value when available; otherwise total property value."
                  />
                  <CurrencyInput
                    label="Claimed exemption"
                    value={details.exemptionAmount}
                    onChange={(value) => updateDetail("exemptionAmount", value)}
                    required
                  />
                  <div className="md:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Statutory exemption *</span>
                    <div className="flex flex-col gap-2 sm:flex-row">
                      <input
                        type="text"
                        value={details.exemptionCode}
                        onChange={(event) => updateDetail("exemptionCode", event.target.value)}
                        className="min-w-0 flex-1 rounded-md border border-gray-300 bg-white p-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => updateDetail("exemptionCode", addTennesseeCodePrefix(details.exemptionCode))}
                        className="rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                      >
                        Add Tenn. Code Ann. §
                      </button>
                    </div>
                    <span className="mt-1 block text-xs text-gray-500">
                      Filled from the property's exemption code; use the button to add the Tennessee citation prefix.
                    </span>
                  </div>
                  <label className="block md:col-span-2">
                    <span className="mb-1 block text-sm font-medium text-gray-700">Property description *</span>
                    <textarea
                      rows="3"
                      value={details.propertyDescription}
                      onChange={(event) => updateDetail("propertyDescription", event.target.value)}
                      className="w-full rounded-md border border-gray-300 bg-white p-2 text-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                    />
                  </label>
                </div>
              </div>

              <div className="rounded-lg bg-white p-6 shadow-md">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Certificate of Service</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      The Chapter 7 Trustee and Attorney for the United States Trustee
                      will be listed as served through the Court's electronic noticing. Choose USM or Certified
                      Mail for each creditor who should also receive the motion.
                    </p>
                  </div>
                  <div className="flex shrink-0 gap-3 text-sm">
                    <button
                      type="button"
                      onClick={() => setServiceMethods(Object.fromEntries(
                        creditors.map((creditor) => [String(creditor.id), "USM"]),
                      ))}
                      className="font-semibold text-indigo-700 hover:text-indigo-900"
                    >
                      Select All as USM
                    </button>
                    <button
                      type="button"
                      onClick={() => setServiceMethods({})}
                      className="font-semibold text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>
                </div>

                <div className="mt-5 max-h-80 overflow-y-auto rounded-md border border-gray-200">
                  {creditors.map((creditor) => {
                    const creditorId = String(creditor.id);
                    const isLienCreditor = creditorId === String(selectedCreditor.id);
                    return (
                      <div
                        key={creditorId}
                        className={`flex flex-col gap-3 border-b border-gray-200 p-3 last:border-b-0 sm:flex-row sm:items-center sm:justify-between ${isLienCreditor ? "bg-indigo-50" : "bg-white"}`}
                      >
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900">
                            {creditor.account_name || "Creditor"}{isLienCreditor ? " — judgment lien creditor" : ""}
                          </p>
                          <p className="mt-1 text-xs text-gray-500">{creditorAddress(creditor)}</p>
                        </div>
                        <select
                          value={serviceMethods[creditorId] || ""}
                          onChange={(event) => {
                            const method = event.target.value;
                            setServiceMethods((current) => {
                              const next = { ...current };
                              if (method) next[creditorId] = method;
                              else delete next[creditorId];
                              return next;
                            });
                          }}
                          className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 sm:w-36"
                        >
                          <option value="">None</option>
                          <option value="USM">USM</option>
                          <option value="Certified">Certified</option>
                        </select>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="rounded-lg bg-white p-6 shadow-md">
                <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-center">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Other liens on this property</h3>
                    <p className="mt-1 text-sm text-gray-600">
                      Secured creditors linked to this property are ordered by date. Earlier liens
                      are checked automatically; later liens are shown but left unchecked so you can confirm priority.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={addOtherLien}
                    className="self-start rounded-md border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50"
                  >
                    Add Other Lien
                  </button>
                </div>

                {!otherLiens.length ? (
                  <p className="mt-5 rounded-md bg-gray-50 p-3 text-sm text-gray-600">
                    No other secured creditors are linked to this property.
                  </p>
                ) : (
                  <div className="mt-5 space-y-4">
                    {otherLiens.map((lien, index) => (
                      <div key={lien.id} className="rounded-md border border-gray-200 p-4">
                        <div className="mb-3 flex items-center justify-between gap-4">
                          <label className="inline-flex items-center gap-2 text-sm font-medium text-gray-700">
                            <input
                              type="checkbox"
                              checked={lien.included !== false}
                              onChange={(event) => updateOtherLien(index, "included", event.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
                            />
                            Include in impairment calculation
                          </label>
                          <button
                            type="button"
                            onClick={() => setOtherLiens((current) => current.filter((_, lienIndex) => lienIndex !== index))}
                            className="text-sm font-medium text-red-600 hover:text-red-800"
                          >
                            Remove
                          </button>
                        </div>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                          <InputField
                            label="Lienholder"
                            value={lien.name}
                            onChange={(value) => updateOtherLien(index, "name", value)}
                          />
                          <InputField
                            label="Nature of lien"
                            value={lien.nature}
                            onChange={(value) => updateOtherLien(index, "nature", value)}
                          />
                          <InputField
                            label="Lien date"
                            type="date"
                            value={lien.date}
                            onChange={(value) => updateOtherLien(index, "date", value)}
                          />
                          <CurrencyInput
                            label="Amount"
                            value={lien.amount}
                            onChange={(value) => updateOtherLien(index, "amount", value)}
                          />
                        </div>
                        {lien.automatic && (
                          <p className="mt-2 text-xs text-gray-500">
                            Filled from a creditor linked to this property. {datePriority(lien.date, details.lienDate)}.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg bg-white p-6 shadow-md">
                <h3 className="text-lg font-semibold text-gray-900">Impairment calculation</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Judicial lien + other liens + exemption − property value = extent of impairment.
                </p>
                <dl className="mt-5 grid grid-cols-2 gap-4 text-sm md:grid-cols-3 lg:grid-cols-6">
                  {[
                    ["Judgment lien (A)", calculation.lienAmount],
                    ["Other liens (B)", calculation.otherLiensTotal],
                    ["Exemption (C)", calculation.exemptionAmount],
                    ["Property value (D)", calculation.propertyValue],
                    ["Impairment (E)", calculation.impairment],
                    ["Lien remaining (F)", calculation.remainingSecuredAmount],
                  ].map(([label, value]) => (
                    <div key={label} className="rounded-md bg-gray-50 p-3">
                      <dt className="text-gray-500">{label}</dt>
                      <dd className="mt-1 font-semibold text-gray-900">{formatLienCurrency(value)}</dd>
                    </div>
                  ))}
                </dl>

                {calculation.avoidableAmount > 0 ? (
                  <div className="mt-5 rounded-md bg-green-50 p-4 text-green-900">
                    <span className="font-semibold">
                      {calculation.fullyAvoided ? "Full avoidance" : "Partial avoidance"}:
                    </span>{" "}
                    {formatLienCurrency(calculation.avoidableAmount)} is avoidable.
                    {!calculation.fullyAvoided && (
                      <> The remaining secured lien is {formatLienCurrency(calculation.remainingSecuredAmount)}.</>
                    )}
                  </div>
                ) : (
                  <div className="mt-5 rounded-md bg-amber-50 p-4 text-amber-900">
                    The current figures do not show any impairment, so the documents cannot yet be prepared.
                  </div>
                )}

                {validationErrors.length > 0 && (
                  <div className="mt-5 rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <p className="font-semibold">Complete or confirm the following:</p>
                    <ul className="mt-2 list-disc space-y-1 pl-5">
                      {validationErrors.map((message) => <li key={message}>{message}</li>)}
                    </ul>
                  </div>
                )}

                <button
                  type="button"
                  onClick={handleGenerate}
                  disabled={generating || validationErrors.length > 0}
                  className="mt-6 inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {generating ? "Preparing and saving packet…" : "Prepare and Save Service Packet"}
                </button>

                {preparedPdfs && (
                  <div className="mt-6 grid grid-cols-1 gap-4 border-t border-gray-200 pt-6 lg:grid-cols-3">
                    <div className="rounded-md border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900">Motion PDF</h4>
                      <p className="mb-4 mt-1 text-sm text-gray-600">
                        Motion, notice, lien tables, impairment calculation, signature, and certificate of service.
                      </p>
                      <button
                        type="button"
                        onClick={() => downloadLienAvoidancePdf(preparedPdfs.motion)}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      >
                        Download Motion PDF
                      </button>
                    </div>
                    <div className="rounded-md border border-gray-200 p-4">
                      <h4 className="font-semibold text-gray-900">Proposed Order PDF</h4>
                      <p className="mb-4 mt-1 text-sm text-gray-600">
                        Separate proposed order identifying the lien and relief requested.
                      </p>
                      <button
                        type="button"
                        onClick={() => downloadLienAvoidancePdf(preparedPdfs.order)}
                        className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700"
                      >
                        Download Proposed Order PDF
                      </button>
                    </div>
                    <div className="rounded-md border border-green-200 bg-green-50 p-4">
                      <h4 className="font-semibold text-gray-900">Complete Service Packet</h4>
                      <p className="mb-4 mt-1 text-sm text-gray-600">
                        Saved motion, proposed order, merged envelopes, and individual envelopes.
                      </p>
                      <button
                        type="button"
                        onClick={() => downloadStoredPacket(preparedPdfs.packet)}
                        disabled={downloadingPacketId === preparedPdfs.packet.id}
                        className="rounded-md bg-green-700 px-4 py-2 text-sm font-semibold text-white hover:bg-green-800 disabled:opacity-60"
                      >
                        {downloadingPacketId === preparedPdfs.packet.id ? "Downloading…" : "Download Service Packet"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

            </>
          )}
        </>
      )}
    </section>
  );
}
